/**
 *
 */
import { IAction } from '..';
import { IBaseState, INITIAL_STATE } from '../base-reducer';
import {
  assign,
  cloneDeep,
  eq,
  get,
  isEmpty,
  isString,
  map,
  omit,
} from 'lodash';

import {
  BULK_UPDATE_ERROR,
  BULK_UPDATE_SUCCESS,
  CLEAR_CURRENT_DOCUMENT,
  DELETE_DOCUMENT_BY_ENTITY_ID,
  DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS,
  DELETE_DOCUMENT_BY_ENTITY_ID_ERROR,
  FETCH_GET_DOCUMENT_BY_ID,
  FETCH_GET_DOCUMENT_BY_ID_ERROR,
  FETCH_GET_DOCUMENT_BY_ID_SUCCESS,
  FETCH_LIST,
  FETCH_LIST_ERROR,
  FETCH_LIST_SUCCESS,
  FETCH_SCHEMA,
  FETCH_SCHEMA_ERROR,
  FETCH_SCHEMA_SUCCESS,
  FETCH_CREATE_ENTITY,
  FETCH_CREATE_ENTITY_ERROR,
  FETCH_CREATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY,
  FETCH_UPDATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY_ERROR,
  FORM_STATUS_CHANGED,
  FORM_VALUE_CHANGED,
  GET_ENTITY_CAPTIONS_SUCCESS,
  SHOW_DOCUMENT,
  STORE_CLEAR_ITEMS,
  STORE_UPDATE_DATA,
  STORE_UPDATE_DOCUMENT,
  STORE_ORDER_STATUS,
  STORE_UPDATE_ITEM,
} from '../../actions';
import { Reducer } from 'redux';
import { ENTITY_TYPES } from '@shared/types';
import { ENTITY_ID_PATH } from '@config/global-constants';

export interface StateType {
  [entityType: string]: IBaseState;
}

export const INITIAL_ENTITIES_STATE: StateType = {};

// eslint-disable-next-line complexity
export const entityReducer: Reducer<StateType> = (
  state: StateType = INITIAL_ENTITIES_STATE,
  action: IAction,
): StateType => {
  const entityType: ENTITY_TYPES = get(action, 'payload.entity_type', null);
  const { payload, type } = action;

  if (!isString(entityType) || isEmpty(entityType)) {
    return state;
  }

  const initialState: IBaseState = cloneDeep(INITIAL_STATE);

  const newEntityState: IBaseState = Object.assign(
    initialState,
    get(state, entityType, {}),
  );

  /**
   * patch helper updates composite 'entities' state and merges changes into entity store
   */
  const patch: (changes: object) => any = (changes): object => {
    return Object.assign({}, state, {
      [entityType]: Object.assign(newEntityState, changes),
    });
  };
  // eslint-disable-next-line
  switch (type) {
    case FETCH_SCHEMA:
      return patch({
        isSchemaRequested: true,
      });

    case FETCH_SCHEMA_SUCCESS:
      return patch({
        schema: payload.response,
        operationResult: payload,
      });

    // TODO: Add option to keep current list on fetch to make gui more fluid
    case FETCH_LIST:
      if (get(payload, 'options.keepUntilFetch')) {
        return state;
      }

      return patch({ items: [], dataRequested: true });

    case STORE_CLEAR_ITEMS:
      return patch({
        items: [],
        total: null,
      });

    case GET_ENTITY_CAPTIONS_SUCCESS:
      const itemCaptions = get(state, [entityType, 'itemCaptions'], {});
      const newItemCaptions = Object.assign(
        {},
        itemCaptions,
        payload.itemCaptions,
      );

      return patch({ itemCaptions: newItemCaptions });

    case FETCH_LIST_SUCCESS:
      return patch({
        items: get(payload, 'response.items', []),
        total: get(payload, 'response.total', 0),
        operationResult: payload,
        previousRequest: get(payload, 'request', {}),
        current_page: get(payload, 'response.current_page', 1),
        page_count: get(payload, 'response.page_count', 0),
        page_size: get(payload, 'response.page_size', 0),
      });

    case FETCH_GET_DOCUMENT_BY_ID:
    case DELETE_DOCUMENT_BY_ENTITY_ID:
    case FETCH_CREATE_ENTITY:
    case FETCH_UPDATE_ENTITY:
      return patch({
        errors: null,
      });

    case CLEAR_CURRENT_DOCUMENT:
      return patch({ currentDocument: null });

    case STORE_UPDATE_ITEM:
      return patch({
        items: map(get(newEntityState, 'items', []), (item) => {
          const isUpdateItem = eq(
            get(item, ENTITY_ID_PATH),
            get(payload, ['entityItem', ENTITY_ID_PATH]),
          );
          if (isUpdateItem) {
            return {
              ...item,
              ...payload.entityItem,
            };
          }

          return item;
        }),
      });

    case STORE_UPDATE_DOCUMENT:
      return patch({
        items: get(newEntityState, 'items', []).map((item) =>
          item.entity_id === payload.entity_id ? payload.document : item,
        ),
      });

    case FETCH_GET_DOCUMENT_BY_ID_SUCCESS:
      const skipUpdateDocument = get(
        payload,
        'options.skipUpdateDocument',
        false,
      );

      return patch(
        assign(
          {},
          { operationResult: payload },
          skipUpdateDocument ? {} : { currentDocument: payload.response },
        ),
      );

    case SHOW_DOCUMENT:
      return patch({
        currentDocument: payload.document,
      });

    case DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS:
    case FETCH_CREATE_ENTITY_SUCCESS:
    case FETCH_UPDATE_ENTITY_SUCCESS:
    case BULK_UPDATE_SUCCESS:
      return patch({
        operationResult: payload,
      });
    case FETCH_GET_DOCUMENT_BY_ID_ERROR:
    case FETCH_LIST_ERROR:
    case FETCH_SCHEMA_ERROR:
    case DELETE_DOCUMENT_BY_ENTITY_ID_ERROR:
    case FETCH_CREATE_ENTITY_ERROR:
    case FETCH_UPDATE_ENTITY_ERROR:
    case BULK_UPDATE_ERROR:
      return patch({
        operationResult: payload,
        errors: get(payload, 'errors', null),
        dataRequested: false,
      });

    case STORE_UPDATE_DATA:
      return patch(payload.data);

    case FORM_VALUE_CHANGED:
    case FORM_STATUS_CHANGED:
      return patch(omit(payload, 'entity_type'));
    case STORE_ORDER_STATUS:
      return patch({
        entity_status: payload.status,
      });
    default:
      return state;
  }
};
